import { FC, ReactNode } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CacheProvider, EmotionCache } from "@emotion/react";
import { materialTheme } from "./materialTheme";
import { createEmotionCache } from "./createEmotionCache";

interface Props {
  children?: ReactNode;
  // Overridden per-request during SSR (see _document.tsx). Falls back to a
  // shared direction-specific client cache so this doesn't need threading
  // through every non-Next.js consumer (e.g. Jest, the storybook Decorator).
  emotionCache?: EmotionCache;
  direction?: "ltr" | "rtl";
}

// Stable, isolated caches let a locale switch reuse previously inserted CSS.
// _document supplies a fresh, direction-matched cache for each server request.
const clientSideEmotionCaches = {
  rtl: createEmotionCache("rtl"),
  ltr: createEmotionCache("ltr"),
};
const materialThemes = {
  rtl: materialTheme,
  ltr: createTheme(materialTheme, { direction: "ltr" }),
};

const MaterialProvider: FC<Props> = ({
  children,
  emotionCache,
  direction = "rtl",
}) => {
  return (
    <CacheProvider value={emotionCache ?? clientSideEmotionCaches[direction]}>
      <ThemeProvider theme={materialThemes[direction]}>
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
};

export { MaterialProvider };
